// Wi-Fi provisioning over BLE — the onboarding step that kept the Flutter app
// alive. Port of bleFunctions.provisionWifiOverBle.
//
// SEQUENCE, and the order matters:
//   1. connect
//   2. SUBSCRIBE FIRST, then write. The device replies within milliseconds and
//      reboots on success, so subscribing after writing loses the reply — the
//      user then sees a timeout on a provision that actually worked.
//   3. write whichever payload `chooseWifiPayload` picks — `WIFI:<ssid>,<pass>`
//      by DEFAULT, with `ADDWIFI ssid=<ssid>&pass=<pass>` only as the fallback
//      for an SSID containing a comma. (This comment used to name ADDWIFI as
//      the payload; that was true before the formats were confirmed against
//      board E072A14582D0 and the default flipped. The code has always deferred
//      to chooseWifiPayload — only the comment was stale.)
//   4. wait for "WIFI SAVED …" / "WIFI PARSE FAILED" / "WIFI SAVE ERROR=…"
//
// A DISCONNECT RIGHT AFTER THE WRITE IS SUCCESS, NOT FAILURE. The firmware
// reboots the moment it saves credentials, which drops the link — often before
// the notification is delivered. Treating that as an error would report failure
// on the happy path, so a drop that arrives after the write counts as success
// unless an explicit failure was seen first.

import { openSession } from './bleSession';
import { chooseWifiPayload, isProvisionFailure, isProvisionSuccess } from './bleConfig';

const REPLY_TIMEOUT_MS = 12000;

/**
 * Send Wi-Fi credentials to a device over BLE.
 *
 * @param deviceId      platform BLE handle
 * @param uniqueNumber  hex MAC — REQUIRED, the auth key derives from it
 * -> { ok, message }
 */
export async function provisionWifiOverBle(
  deviceId, uniqueNumber, ssid, password, onProgress, ownerSecret = null,
) {
  const say = (m) => { if (onProgress) onProgress(m); };

  // Decide the payload BEFORE connecting: credentials that neither form can
  // carry are a dead end, and finding that out after a connect and a handshake
  // wastes the user's time to reach the same answer.
  const chosen = chooseWifiPayload(ssid, password);
  if (!chosen.ok) return { ok: false, message: chosen.error };

  say('Connecting…');
  let sawFailure = false;
  let wrote = false;
  let settle = null;

  const done = new Promise((resolve) => { settle = resolve; });
  let timer = null;
  const finish = (ok, message) => {
    if (!settle) return;
    clearTimeout(timer);
    const s = settle;
    settle = null;
    s({ ok, message });
  };

  const opened = await openSession(deviceId, uniqueNumber, {
    ownerSecret,
    onStatus: (text) => {
      if (isProvisionSuccess(text)) {
        finish(true, 'Wi-Fi saved. The device is restarting and will join your network.');
      } else if (isProvisionFailure(text)) {
        sawFailure = true;
        finish(false, `Device rejected the credentials: ${text}`);
      }
    },
    // ⚠️ A DROP RIGHT AFTER THE WRITE IS SUCCESS. The firmware reboots ~600 ms
    // after saving, often before its notification is delivered. Reporting that
    // as an error would fail the happy path.
    onDisconnect: () => {
      if (wrote && !sawFailure) {
        finish(true, 'Wi-Fi saved. The device is restarting and will join your network.');
      } else {
        finish(false, 'The device disconnected before Wi-Fi could be saved.');
      }
    },
  });

  if (!opened.ok) return { ok: false, message: opened.error };

  timer = setTimeout(() => {
    finish(false, 'No reply from the device. Move closer and try again.');
  }, REPLY_TIMEOUT_MS);

  try {
    say('Sending credentials…');
    await opened.session.send(chosen.payload);
    wrote = true;
    say('Waiting for the device to confirm…');
  } catch (e) {
    finish(false, (e && e.message) || 'Bluetooth error');
  }

  const result = await done;
  await opened.session.close();
  return result;
}
